import { defineStore } from "pinia";
import { fetchSettings, updateSettings } from "@/api/settings";

const LS_KEY = "app.settings";

function loadLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveLocal(s) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch {}
}

export const useSettingsStore = defineStore("settings", {
  state: () => ({
    ready: false,
    settingId: null,
    crs: { code: 4326, name: "WGS84 (Lat/Lon)", proj4Def: "", region: "", displayOrder: 1 },
    measurement: 1, // 1 metric, 2 imperial
    darkMode: false,
    guidedTour: true,
  }),
  getters: {
    measurementLabel(state) {
      return state.measurement === 2 ? "imperial" : "metric";
    },
  },
  actions: {
    async init() {
      // 1) local first (instant UI)
      const local = loadLocal();
      if (local) Object.assign(this, local);

      // 2) server authoritative
      try {
        const s = await fetchSettings();
        this.settingId = s.settingId ?? null;
        this.crs = s.crs ?? this.crs;
        this.measurement = Number(s.measurement ?? this.measurement);
        this.darkMode = !!s.darkMode;
        this.guidedTour = !!s.guidedTour;
        saveLocal(this.$state);
      } catch (e) {
        // offline / unauth: keep local
      } finally {
        this.ready = true;
      }
    },

    async save(partial) {
      // prepare payload for API
      const payload = {
        crsOption: partial?.crs?.code ?? this.crs.code,
        measurement: partial?.measurement ?? this.measurement,
        darkMode: partial?.darkMode ?? this.darkMode,
        guidedTour: partial?.guidedTour ?? this.guidedTour,
      };

      const s = await updateSettings(payload);
      // update local state from server response
      this.settingId = s.settingId ?? this.settingId;
      this.crs = s.crs ?? this.crs;
      this.measurement = Number(s.measurement ?? this.measurement);
      this.darkMode = !!s.darkMode;
      this.guidedTour = !!s.guidedTour;

      saveLocal(this.$state);
      return s;
    },

    // handy setters
    setCrs(code, name) {
      this.crs = { ...(this.crs || {}), code, name };
      saveLocal(this.$state);
    },
    setMeasurement(val) {
      this.measurement = Number(val); // 1 or 2
      saveLocal(this.$state);
    },
    setDarkMode(val) {
      this.darkMode = !!val;
      saveLocal(this.$state);
    },
    setGuidedTour(val) {
      this.guidedTour = !!val;
      saveLocal(this.$state);
    },
  },
});
